import { TrashIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import TableLayout, { TableNullState } from 'dashboard/layouts/TableLayout';
import {
  ExternalIntegration,
  ExternalIntegrationAttributes,
} from 'dashboard/models/external-integration';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Table, {
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { paragraphMixins } from 'design-system/mixins/text';
import { LegacyExternalIntegrationVendor } from 'lib/constants/externalIntegrationVendors';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';

const DisplayInput = styled.input`
  ${paragraphMixins.md};
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  width: 100%;
  color: ${({ theme }) => theme.color.text.primary};
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface ApiKeysTableProps {
  editable: boolean;
  isLoading: boolean;
  externalIntegrations: ExternalIntegration[];
  externalIntegrationVendor: LegacyExternalIntegrationVendor;
  setSelectedIntegration: (integration: ExternalIntegration) => void;
}

const ApiKeysTable = ({
  editable,
  isLoading,
  externalIntegrations,
  externalIntegrationVendor,
  setSelectedIntegration,
}: ApiKeysTableProps) => {
  const [masked, { toggle: toggleMask }] = useToggle(true);
  // concat an extra column at the end for the archive button
  const tableHeaders = externalIntegrationVendor['input-fields']
    .map((field) => field.label)
    .concat([`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`, '']);

  return (
    <>
      <HeaderContainer>
        <Card.Title>API Keys</Card.Title>
        {externalIntegrations.length > 0 && (
          <ToggleSlider value={!masked} labelOn='Show Keys' onChange={toggleMask} />
        )}
      </HeaderContainer>
      <Table>
        <TableLayout
          isLoading={isLoading}
          showHeader={true}
          isEmpty={externalIntegrations.length === 0}
          nullState={
            <TableNullState>
              Looks like there aren&apos;t any {externalIntegrationVendor['display-name']} API keys
              setup yet
            </TableNullState>
          }
          headerTR={<></>}
        >
          <TableRow>
            {tableHeaders.map((header, index) => (
              <TableHeaderCell key={index}>{header}</TableHeaderCell>
            ))}
          </TableRow>
          <TableBody>
            {externalIntegrations.map((integration) => (
              <TableRow key={`external-integration-${integration.id}`}>
                {externalIntegrationVendor['input-fields'].map((field, index) =>
                  (field.config as Record<string, any>)?.['is-secret'] ? (
                    <TableCell key={index}>
                      <DisplayInput
                        value={
                          integration.attributes[field.key as keyof ExternalIntegrationAttributes]
                        }
                        readOnly
                        type={masked ? 'password' : 'text'}
                      />
                    </TableCell>
                  ) : (
                    <TableCell key={index}>
                      {integration.attributes[field.key as keyof ExternalIntegrationAttributes]}
                    </TableCell>
                  ),
                )}
                <TableCell> {integration.attributes['created-at']} </TableCell>
                <TableCell>
                  <Button
                    width='icon'
                    variant='basic'
                    type='button'
                    onClick={() => setSelectedIntegration(integration)}
                    disabled={!editable}
                    id={`delete-button-${integration.id}`}
                    aria-label='delete button'
                  >
                    <TrashIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableLayout>
      </Table>
    </>
  );
};

export default ApiKeysTable;
